import { isCreditTerms } from '@/features/quotes/quoteDraft'
import { modeFromCode } from '@/lib/icons'
import { carriedPaymentOptionValues } from '@/lib/paymentOptions'
import type {
  Money,
  QuoteDetail,
  QuoteTeamMember,
  TransportMode,
} from '@/types/domain'

/* What the case WILL be, read off the quote before anybody presses Create (QW-11).
 *
 * A DISPLAY MIRROR of the conversion transaction, and nothing more. Every field
 * below restates one decision the server makes, so the confirmation can say it
 * before the act rather than leave the reader to discover it on the case:
 *
 *   · the route, mode and transit — `selectedRoute`
 *     (backend/internal/quote/conversion.go): the selected option wins, the
 *     header's mode stands in for an option with none, and the transit is taken
 *     as a PAIR, the header's only when the option states neither bound;
 *   · the planned leg — `planLeg` (backend/cmd/asa/conversion.go): none without
 *     a selected option or without a mode;
 *   · the payment terms — `caseTermsKind` (same file): three choices carried as
 *     themselves, CREDIT carried as `credit` with its days (S-12) — the
 *     quote's own, or the ones this sheet asks for when it states none —
 *     everything else as `other`;
 *   · the rate — set only when the costing's sell total is positive;
 *   · the planned costs — one per line whose buy total is positive (0018's
 *     amount > 0);
 *   · the team — the quote's owner plus its sales-role members
 *     (`salesTeamUnscoped`, backend/internal/quote/repo.go).
 *
 * It is NOT a permission check and gates nothing: the server re-reads the quote
 * inside its own transaction and decides, and the response — `legCreated`,
 * `rateSet`, `plannedLines` — is the truth once the case exists. Where the two
 * could disagree the preview says less rather than more: an entity it cannot
 * resolve is omitted, a team it has not read is undefined, a mode it has no
 * word for is absent.
 *
 * Counts and colleagues' names only. A cost line's vendor never reaches this
 * shape (FR-20 keeps suppliers out of the quote's hand-over).
 */

/** Whether conversion will plan the case's first leg, and if not, why. */
export type ConversionLeg = 'planned' | 'no-option' | 'no-mode'

export interface ConversionPreview {
  clientName: string
  /** The quote's issuing entity by name, or absent when it is not among the
   *  active ones this reader can list — never a guess at why. */
  entityName?: string
  /** The stored terms, trimmed as the server trims them; `''` when none. */
  paymentTerms: string
  /** False when the case will hold these terms as `other`: one instalment
   *  dated at registration. True for CREDIT (S-12), with or without days: the
   *  days are the quote's, or the sheet asks for them (`asksCreditDays`). */
  paymentTermsCarried: boolean
  /** The quote's own credit days, when it states them (S-12). */
  paymentTermsCreditDays?: number
  /** A CREDIT quote that states no days: the sheet asks «Дней отсрочки», and
   *  the server refuses the conversion without them (422 `required`). */
  asksCreditDays: boolean
  incoterm?: string
  /** Absent when nothing names a mode the client knows — rendered as a dash,
   *  never as the legs' multimodal fallback. */
  mode?: TransportMode
  transitDaysMin?: number
  transitDaysMax?: number
  leg: ConversionLeg
  /** The selected option's two ends, which the planned leg runs between. */
  route?: { origin: string; destination: string }
  /** The agreed rate the case will carry, exactly as served — absent when the
   *  sell total is not positive and no rate will be set. */
  rate?: Money
  /** How many cost lines become planned expense lines. */
  plannedLines: number
  /** Who will be on the case, owner first. Undefined while the team is not
   *  known — which is not the same answer as nobody. */
  team?: string[]
}

/** A decimal STRING is above zero — read by its sign and digits, never parsed
 *  to a float (AD-9). A sum would be arithmetic on money; this is a sign test. */
function isPositive(amount: string | undefined): boolean {
  const value = amount?.trim() ?? ''
  if (value === '' || value.startsWith('-')) return false
  return /[1-9]/.test(value)
}

/** An optional wire id, with the empty string read as absent. */
function present(value: string | undefined): value is string {
  return value !== undefined && value !== ''
}

export function conversionPreview(
  record: QuoteDetail,
  team: QuoteTeamMember[] | undefined,
  entities: { id: string; name: string }[] | undefined,
): ConversionPreview {
  const option = (record.routeOptions ?? []).find((candidate) => candidate.selected)

  // selectedRoute: the option's mode, the header's when the option has none.
  // The CODE is read beside the id it came with, so the label never belongs to
  // a different source from the leg's decision.
  const optionHasMode = present(option?.transportModeId)
  const modeId = optionHasMode ? option?.transportModeId : record.transportModeId
  const modeCode = optionHasMode ? option?.transportModeCode : record.transportModeCode

  // Pairwise, never one bound from each source.
  const optionStatesTransit =
    option !== undefined &&
    (option.transitDaysMin != null || option.transitDaysMax != null)
  const transit = optionStatesTransit
    ? { min: option.transitDaysMin, max: option.transitDaysMax }
    : { min: record.transitDaysMin, max: record.transitDaysMax }

  const leg: ConversionLeg = !option
    ? 'no-option'
    : present(modeId)
      ? 'planned'
      : 'no-mode'

  const paymentTerms = record.paymentTerms?.trim() ?? ''

  const sell = record.costTotals?.sell

  return {
    clientName: record.clientName,
    entityName: present(record.entityId)
      ? entities?.find((entity) => entity.id === record.entityId)?.name
      : undefined,
    paymentTerms,
    /* CREDIT is decided here on its own, whatever the carried list says: it
       is carried WITH a day count, which the list cannot express. */
    paymentTermsCarried:
      isCreditTerms(paymentTerms) ||
      carriedPaymentOptionValues.some((value) => value === paymentTerms),
    paymentTermsCreditDays: isCreditTerms(paymentTerms)
      ? record.paymentTermsCreditDays
      : undefined,
    asksCreditDays:
      isCreditTerms(paymentTerms) && record.paymentTermsCreditDays === undefined,
    incoterm: present(record.incoterm) ? record.incoterm : undefined,
    mode: present(modeId) ? modeFromCode(modeCode) : undefined,
    transitDaysMin: transit.min ?? undefined,
    transitDaysMax: transit.max ?? undefined,
    leg,
    route: option ? { origin: option.origin, destination: option.destination } : undefined,
    rate: sell && isPositive(sell.amount) ? sell : undefined,
    plannedLines: (record.costLines ?? []).filter((line) =>
      isPositive(line.buyTotal?.amount),
    ).length,
    team: team ? caseTeam(record, team) : undefined,
  }
}

/** The owner, then every OTHER sales-role member in the team's order.
 *
 *  The role comparison is the one place this client names a role, and it is a
 *  mirror rather than a gate: the server's own query filters `u.role = 'sales'`
 *  (quote.RoleSales), and the sheet is saying what that query will return. It
 *  decides no control — capabilities do that, through lib/can.ts. */
function caseTeam(record: QuoteDetail, team: QuoteTeamMember[]): string[] {
  const ownerMember = team.find(
    (member) => member.owner || member.userId === record.ownerId,
  )
  const ownerName = record.ownerName || ownerMember?.name
  const sales = team
    .filter(
      (member) =>
        member.role === 'sales' && !member.owner && member.userId !== record.ownerId,
    )
    .map((member) => member.name)
  return ownerName ? [ownerName, ...sales] : sales
}
